import { useCallback, useMemo } from 'react';

export interface RangeSliderProps {
  min: number;
  max: number;
  minValue: number;
  maxValue: number;
  step?: number;
  labels?: number[];
  showValues?: boolean;
  disabled?: boolean;
  /**
   * Which surface the slider sits on. `light` is the cream/paper card of the difficulty picker;
   * `felt` is inside the table frame, where the green action scale disappears into the felt and
   * brass is the surface's own accent (feature 023).
   *
   * Only the colours change — geometry, ARIA and behaviour are identical, so the e2e assertions on
   * the underlying `input[type=range]` hold for both.
   */
  variant?: 'light' | 'felt';
  onChange: (min: number, max: number) => void;
}

/**
 * A dual-handle range slider component for selecting a range of values.
 * Supports single-value selection (min === max).
 * Features ARIA accessibility and keyboard navigation.
 * Uses CSS media queries for responsive sizing (no JS viewport detection).
 */
export default function RangeSlider({
  min,
  max,
  minValue,
  maxValue,
  step = 1,
  labels,
  showValues = false,
  disabled = false,
  variant = 'light',
  onChange,
}: RangeSliderProps) {
  const onFelt = variant === 'felt';
  // Calculate percentage for track fill
  const minPercent = useMemo(
    () => ((minValue - min) / (max - min)) * 100,
    [minValue, min, max]
  );
  const maxPercent = useMemo(
    () => ((maxValue - min) / (max - min)) * 100,
    [maxValue, min, max]
  );

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMin = Math.min(Number(e.target.value), maxValue);
      onChange(newMin, maxValue);
    },
    [maxValue, onChange]
  );

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMax = Math.max(Number(e.target.value), minValue);
      onChange(minValue, newMax);
    },
    [minValue, onChange]
  );

  // Generate labels array if not provided
  const displayLabels = useMemo(() => {
    if (labels) return labels;
    const result: number[] = [];
    for (let i = min; i <= max; i += step) {
      result.push(i);
    }
    return result;
  }, [labels, min, max, step]);

  // Common slider styles (static Tailwind classes that will be compiled)
  const sliderBaseClass = `absolute w-full appearance-none bg-transparent pointer-events-none focus:outline-none`;
  // Per-surface colours. Written as whole class strings rather than interpolated fragments so
  // Tailwind's scanner sees every one of them.
  const trackClass = onFelt ? 'bg-felt-deep' : 'bg-line';
  const fillClass = disabled
    ? (onFelt ? 'bg-[#6b6752]' : 'bg-[#c9c1ab]')
    : (onFelt ? 'bg-brass' : 'bg-primary-500');
  const thumbClass = disabled
    ? (onFelt ? 'range-slider-thumb-felt-disabled' : 'range-slider-thumb-disabled')
    : (onFelt ? 'range-slider-thumb-felt' : 'range-slider-thumb');
  // Cream-soft, never `text-brass`: brass on felt is 3.5:1, under AA for text (index.css ledger).
  const labelClass = onFelt ? 'text-sand' : 'text-ink-soft';
  const valueClass = onFelt ? 'text-brass-on-felt' : 'text-primary-600';

  return (
    // CSS media queries: short: variant applies at viewport height < 750px
    <div className="w-full px-2 short:px-1">
      {/* Slider track and handles - h-6 (24px) accommodates 24px touch thumbs without overflow */}
      <div className="relative h-6">
        {/* Background track */}
        <div className={`absolute w-full h-1 top-[0.625rem] rounded-full ${trackClass}`} />

        {/* Active range fill */}
        <div
          className={`absolute h-1 top-[0.625rem] rounded-full ${fillClass}`}
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
          }}
        />

        {/* Min slider */}
        <input
          type="range"
          min={min}
          max={max}
          value={minValue}
          step={step}
          disabled={disabled}
          onChange={handleMinChange}
          aria-label="Minimum value"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={minValue}
          className={`${sliderBaseClass} h-1 top-[0.625rem] ${thumbClass}`}
          style={{
            zIndex: minValue >= maxValue - 1 ? 5 : 3,
          }}
        />

        {/* Max slider */}
        <input
          type="range"
          min={min}
          max={max}
          value={maxValue}
          step={step}
          disabled={disabled}
          onChange={handleMaxChange}
          aria-label="Maximum value"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={maxValue}
          className={`${sliderBaseClass} h-1 top-[0.625rem] ${thumbClass}`}
          style={{
            zIndex: 4,
          }}
        />
      </div>

      {/* Labels. Two mechanisms, and they are NOT alternatives to each other — each covers what the
          other cannot (FR-033d, constitution VI: >= 12px rendered at every viewport).

          RECLAIM, where the row costs height that a viewport-locked layout has already spent. A host
          surface hides the row outright via `range-slider-labels`: index.css does it on the three
          bands where the frame's `controls` row is inline on a screen with nothing to spare — tall
          portrait phones, landscape phones >= 400h, and short wide desktops. That is the 2026-07-31
          decision and it still stands where it applies: on a 400-550h landscape phone this row is
          ~14px, and the band exists precisely because the height there goes to the panel ROW, so
          reclaiming frees height where raising would spend it. Nothing is lost — the range is printed
          beside the label, each handle announces its own aria-valuenow, and the masthead trigger
          states the range too.

          SIZE, at a flat `text-xs`, for everywhere the reclaim cannot reach or does not want to. The
          row used to carry a `short:` variant of 0.625rem — 10px, under the floor — and `short` is
          `max-height: 750px`, far wider than the three reclaim bands. (Written out rather than as the
          class itself: Tailwind scans this file as plain text, so quoting the literal here would keep
          generating the dead utility.) Two surfaces were left drawing
          10px: the masthead's popup copy of the control, which is not inside `.table-controls` and so
          no reclaim rule can select it, and the pre-session difficulty picker, which is an ordinary
          scrolling page rather than a locked frame. Both have the room; only the height-starved
          inline row does not, and that one is reclaimed rather than shrunk. Do not re-add a `short:`
          size here, and do not extend the reclaim to the popup or the picker — the two mechanisms
          are aimed at different problems. */}
      <div className="range-slider-labels relative flex justify-between mt-1 short:mt-0.5 px-1">
        {displayLabels.map((label) => (
          <span
            key={label}
            className={`text-xs font-medium ${labelClass}`}
            style={{
              position: displayLabels.length > 2 ? 'relative' : 'static',
            }}
          >
            {label}
          </span>
        ))}
      </div>

      {/* Current value display */}
      {showValues && (
        <div className={`flex justify-between mt-2 short:mt-0.5 text-sm short:text-xs font-semibold ${valueClass}`}>
          <span>{minValue}</span>
          <span>{maxValue}</span>
        </div>
      )}

      {/* CSS for slider thumbs - FR-018: dynamic rem units for zoom responsiveness */}
      <style>{`
        .range-slider-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          pointer-events: auto;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: var(--primary-600);
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          border: 2px solid white;
        }
        .range-slider-thumb::-webkit-slider-thumb:hover {
          background: var(--primary-700);
        }
        .range-slider-thumb:focus-visible::-webkit-slider-thumb {
          box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 0 0 3px rgba(11, 107, 58, 0.5);
        }
        .range-slider-thumb::-moz-range-thumb {
          pointer-events: auto;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: var(--primary-600);
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          border: 2px solid white;
        }
        .range-slider-thumb::-moz-range-thumb:hover {
          background: var(--primary-700);
        }
        .range-slider-thumb:focus-visible::-moz-range-thumb {
          box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 0 0 3px rgba(11, 107, 58, 0.5);
        }
        .range-slider-thumb-disabled::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          pointer-events: none;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: #c9c1ab;
          cursor: not-allowed;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          border: 2px solid white;
        }
        .range-slider-thumb-disabled::-moz-range-thumb {
          pointer-events: none;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: #c9c1ab;
          cursor: not-allowed;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          border: 2px solid white;
        }
        /* Felt variant: brass handles ringed in cream. The white ring of the light variant reads
           as a hole punched in the felt, and the green handle vanishes into it. The focus ring is
           cream rather than green for the same reason — it has to be visible against #145737. */
        .range-slider-thumb-felt::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          pointer-events: auto;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: var(--brass);
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.45);
          border: 2px solid var(--cream);
        }
        .range-slider-thumb-felt::-webkit-slider-thumb:hover {
          background: #d8b23a;
        }
        .range-slider-thumb-felt:focus-visible::-webkit-slider-thumb {
          box-shadow: 0 1px 3px rgba(0,0,0,0.45), 0 0 0 3px rgba(251, 247, 236, 0.55);
        }
        .range-slider-thumb-felt::-moz-range-thumb {
          pointer-events: auto;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: var(--brass);
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.45);
          border: 2px solid var(--cream);
        }
        .range-slider-thumb-felt::-moz-range-thumb:hover {
          background: #d8b23a;
        }
        .range-slider-thumb-felt:focus-visible::-moz-range-thumb {
          box-shadow: 0 1px 3px rgba(0,0,0,0.45), 0 0 0 3px rgba(251, 247, 236, 0.55);
        }
        .range-slider-thumb-felt-disabled::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          pointer-events: none;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: #6b6752;
          cursor: not-allowed;
          box-shadow: 0 1px 3px rgba(0,0,0,0.45);
          border: 2px solid #a8a290;
        }
        .range-slider-thumb-felt-disabled::-moz-range-thumb {
          pointer-events: none;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: #6b6752;
          cursor: not-allowed;
          box-shadow: 0 1px 3px rgba(0,0,0,0.45);
          border: 2px solid #a8a290;
        }
      `}</style>
    </div>
  );
}
